import { useMutation } from "@tanstack/react-query";
import type { JSX } from "react";
import { useI18n } from "../../shared/i18n/I18nContext";
import type { MessageKey } from "../../shared/i18n/messages.de";
import { useOwnerKey } from "../../shared/session/tenantSelection";
import { cx } from "../../shared/ui/classNames";
import { ErrorMessage } from "../../shared/ui/ErrorMessage";
import styles from "../../shared/ui/ui.module.css";
import type { GeofenceStatus } from "../../platform/PlatformServices";
import type { GeofenceReminderControl } from "./useGeofenceReminder";
import { useGeofenceReminder } from "./useGeofenceReminder";

/**
 * Lists what keeps the reminder from working on this device.
 *
 * @param control - The reminder.
 * @param status - The status of the device.
 * @returns The keys of the hints.
 */
function hintsOf(control: GeofenceReminderControl, status: GeofenceStatus): MessageKey[] {
  const hints: MessageKey[] = [];
  const answer = control.answer;

  if (status.notifications === "denied") {
    hints.push("geofence.hint.notifications");
  }

  if (status.mode === "regions") {
    if (status.location !== "granted") {
      hints.push("geofence.hint.location");
    } else if (status.background !== "granted") {
      hints.push("geofence.hint.background");
    } else if (!status.precise) {
      hints.push("geofence.hint.precise");
    }

    if (answer !== null && answer.regions.length === 0) {
      hints.push("geofence.hint.noRegion");
    }
  } else if (answer !== null && answer.shiftStarts.length === 0) {
    hints.push("geofence.hint.noShift");
  }

  return hints;
}

/**
 * The consent texts for what the device does: regions with the location „while using the app“
 * (iOS), regions with the location in the background (Android stage „geofence“, the notice Google
 * Play asks for before the system dialog) or reminders at the shift start without any location.
 *
 * @param status - The status of the device.
 * @param oneShotRegions - Whether regions remind once per arming (iOS).
 * @returns The keys of the paragraphs.
 */
function consentTextsOf(status: GeofenceStatus, oneShotRegions: boolean): MessageKey[] {
  if (status.mode === "shiftStart") {
    const shiftStart: MessageKey[] = ["geofence.consent.shiftStart", "geofence.consent.voluntary"];
    return shiftStart;
  }

  const device: MessageKey = oneShotRegions
    ? "geofence.consent.ios"
    : "geofence.consent.background";
  const regions: MessageKey[] = ["geofence.consent.regions", device, "geofence.consent.voluntary"];

  return regions;
}

/**
 * The geofence reminder in the settings of the native apps (spec 12.4, T-188, O-34, ADR-0167):
 * shown only while the employer released it. The person reads what the device does and switches it
 * on (consent, kept only on this device) or off at any time without a disadvantage; the employer does
 * not learn either. Hints name a missing permission and open the settings of the system.
 *
 * @returns The settings section, or `null`.
 */
export function GeofenceReminderSettings(): JSX.Element | null {
  const { t } = useI18n();
  const owner = useOwnerKey();
  const control = useGeofenceReminder(owner);
  const change = useMutation({
    mutationFn: (enable: boolean) => (enable ? control.enable() : control.disable()),
  });
  const reminders = control.reminders;
  const status = control.status;

  if (reminders === null || control.answer?.enabled !== true || status === null) {
    return null;
  }

  const consented = control.consent !== null;
  const title = status.mode === "shiftStart" ? t("geofence.titleShiftStart") : t("geofence.title");
  const hints = consented ? hintsOf(control, status) : [];
  const settingsHint = hints.some(
    (hint) => hint !== "geofence.hint.noRegion" && hint !== "geofence.hint.noShift",
  );

  const section = (
    <section aria-labelledby="geofence-settings" className={cx(styles["card"], styles["stack"])}>
      <h2 id="geofence-settings">{title}</h2>
      {consented ? (
        <p role="status">{t("geofence.state.on")}</p>
      ) : (
        consentTextsOf(status, reminders.oneShotRegions).map((key) => <p key={key}>{t(key)}</p>)
      )}
      {hints.map((key) => (
        <p key={key} className={styles["muted"]}>
          {t(key)}
        </p>
      ))}
      <div className={styles["row"]}>
        <button
          type="button"
          className={cx(styles["button"], consented ? undefined : styles["primary"])}
          disabled={change.isPending}
          onClick={() => {
            change.mutate(!consented);
          }}
        >
          {consented ? t("geofence.disable") : t("geofence.enable")}
        </button>
        {settingsHint ? (
          <button
            type="button"
            className={styles["button"]}
            onClick={() => {
              void reminders.openSettings();
            }}
          >
            {t("geofence.openSettings")}
          </button>
        ) : null}
      </div>
      {change.isError ? <ErrorMessage error={change.error} /> : null}
    </section>
  );

  return section;
}
